import React, { useState } from "react";
import LegalHeader from "../components/LegalHeader";
import Footer from "../components/Footer";

export default function ServicePolicy() {
  const [activeSection, setActiveSection] = useState<string>("intro");

  const scrollTo = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -90;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const sections = [
    { id: "intro", title: "1. Scope of Services" },
    { id: "diagnosis", title: "2. Diagnostic Visit & Quotations" },
    { id: "warranty", title: "3. 30-Day Revisit Warranty" },
    { id: "parts", title: "4. Spare Parts Policy" },
    { id: "safety", title: "5. Safety & Customer Guidelines" },
    { id: "contact", title: "6. Customer Assistance" },
  ];

  return (
    <div className="w-full bg-white min-h-screen flex flex-col font-['Roboto',sans-serif]">
      <LegalHeader />

      {/* Header Title Section */}
      <section className="border-b border-gray-200 py-10 px-6">
        <div className="max-w-[1100px] mx-auto">
          <p className="text-xs text-gray-500 mb-2">Fixezy / Legal / Service Policy</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
            Service Policy
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Last updated: October 2026 • Operating standards and warranty coverage by Fixezy Private Limited
          </p>
        </div>
      </section>

      {/* Main Content Layout */}
      <div className="max-w-[1100px] mx-auto px-6 py-10 pb-20 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* Table of Contents */}
          <aside className="hidden lg:block lg:col-span-4 sticky top-28 border border-gray-200 rounded-lg p-5 bg-white">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-4">
              Table of Contents
            </p>
            <nav className="flex flex-col space-y-2">
              {sections.map((section) => (
                <p
                  key={section.id}
                  onClick={() => scrollTo(section.id)}
                  className={`cursor-pointer text-xs transition-colors ${activeSection === section.id
                      ? "text-black font-semibold"
                      : "text-gray-600 hover:text-black"
                    }`}
                >
                  {section.title}
                </p>
              ))}
            </nav>
          </aside>

          {/* Legal Text Area */}
          <main className="lg:col-span-8 text-gray-700 space-y-10 leading-relaxed text-sm sm:text-base">
            {/* Section 1 */}
            <section id="intro" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                1. Scope of Services
              </h2>
              <p>
                Fixezy Private Limited ("Fixezy", "we", "us", or "our") connects households and businesses with verified, independent service professionals for appliance repair, plumbing, electrical installations, home cleaning, and general maintenance.
              </p>
              <p className="mt-3">
                Our objective is to deliver reliable, on-time, and transparent home services delivered directly to your doorstep.
              </p>
            </section>

            {/* Section 2 */}
            <section id="diagnosis" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                2. Diagnostic Visit & Quotations
              </h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>
                  <strong>Inspection Charge:</strong> A standard diagnostic/visiting charge is applicable for assessing appliances or home fixtures. If you choose to proceed with the recommended repair, this inspection fee is adjusted against your final bill.
                </li>
                <li>
                  <strong>Digital Job Card Approval:</strong> Technicians will diagnose the defect and present an itemized digital quotation in the app. No repair work begins without your prior consent.
                </li>
                <li>
                  <strong>Transparent Invoicing:</strong> After job completion, an official GST tax invoice is generated directly in the Fixezy app.
                </li>
              </ul>
            </section>

            {/* Section 3 */}
            <section id="warranty" className="scroll-mt-28 border-t border-gray-200 pt-6">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                3. 30-Day Revisit Warranty
              </h2>
              <p>
                We stand behind our quality of workmanship with a <strong>30-Day Revisit Guarantee</strong> on eligible repair jobs:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1.5 text-gray-700">
                <li>If the exact same issue reoccurs within 30 calendar days from the completion date, a technician will revisit your premises to inspect and correct the issue at no additional labor cost.</li>
                <li>
                  <strong>Exclusions:</strong> Warranty does not cover defects caused by external voltage surges, lightning, physical damage, unauthorized tampering or repair attempts by non-Fixezy mechanics after our visit, or normal wear and tear of aged equipment.
                </li>
              </ul>
            </section>

            {/* Section 4 */}
            <section id="parts" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                4. Spare Parts Policy
              </h2>
              <p>
                When components require replacement during servicing:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1.5 text-gray-700">
                <li>Technicians source genuine, compatible parts with upfront price communication.</li>
                <li>Warranties on replacement parts are provided by the respective spare part manufacturer.</li>
                <li>If the customer provides their own replacement parts, Fixezy provides zero warranty on the durability or performance of such parts.</li>
              </ul>
            </section>

            {/* Section 5 */}
            <section id="safety" className="scroll-mt-28">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                5. Safety & Customer Guidelines
              </h2>
              <ul className="list-disc list-inside space-y-1.5 text-gray-700">
                <li>A responsible adult (18+) must be present during the entire service appointment.</li>
                <li>Customers are requested to provide safe access to the work area and safeguard personal valuables, cash, and jewelry.</li>
                <li>Fixezy enforces a zero-tolerance policy against any verbal abuse, harassment, or unsafe physical environment directed at our service partners.</li>
              </ul>
            </section>

            {/* Section 6 */}
            <section id="contact" className="scroll-mt-28 border-t border-gray-200 pt-6">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                6. Customer Assistance
              </h2>
              <p className="text-sm text-gray-600 mb-3">
                For questions regarding your service booking, warranty claims, or service feedback:
              </p>
              <div className="border border-gray-200 rounded-lg p-4 space-y-1 text-sm text-gray-800">
                <p><strong>Fixezy Private Limited</strong></p>
                <p>Vijayanagar, Bengaluru, Karnataka - 560040, India</p>
                <p>Email: support@fixezy.in</p>
                <p>Hours: Mon – Fri, 9:00 AM – 6:00 PM IST</p>
              </div>
            </section>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
}
